// Reusable "Download catalogue" CTA with anonymous lead-capture modal.
// On click → opens email-only mini form. On submit → POST /api/catalog/request
// (creates/upgrades a CRM contact with tag `geovinum_catalog_download` +10pts),
// then opens the PDF in a new tab.
// User can also click "Skip — just download" to bypass capture (still opens PDF).
import { useState } from "react";
import { Download, X } from "lucide-react";
import { useLang, useT } from "@/lib/i18n";
import { apiFetch, API } from "@/lib/api";

const CATEGORY_KEYS = [
  "domaines_viticoles", "cavistes", "negociants", "restaurateurs",
  "distributeurs", "importateurs", "ecoles_du_vin", "drinks_tech",
  "e_commerce", "autre",
];

const CATEGORY_LABEL_KEYS = {
  domaines_viticoles: "catalogCapture.cat.domaines",
  cavistes: "catalogCapture.cat.cavistes",
  negociants: "catalogCapture.cat.negociants",
  restaurateurs: "catalogCapture.cat.restaurateurs",
  distributeurs: "catalogCapture.cat.distributeurs",
  importateurs: "catalogCapture.cat.importateurs",
  ecoles_du_vin: "catalogCapture.cat.ecoles",
  drinks_tech: "catalogCapture.cat.drinks_tech",
  e_commerce: "catalogCapture.cat.e_commerce",
  autre: "catalogCapture.cat.autre",
};

export const DownloadCatalogButton = ({
  variant = "primary",
  productSlug = null,
  className = "",
  testId = "cta-catalog-download",
}) => {
  const t = useT();
  const lang = useLang();
  const [open, setOpen] = useState(false);
  const productPdfUrl = productSlug
    ? `${API}/api/catalog/pdf/product/${productSlug}?lang=${lang}`
    : `${API}/api/catalog/pdf?lang=${lang}`;
  const label = productSlug ? t("cta.product_catalog") : t("cta.full_catalog");

  const onClick = (e) => {
    e.preventDefault();
    setOpen(true);
  };

  const buttonProps = {
    onClick,
    "data-testid": testId,
    "aria-haspopup": "dialog",
  };

  return (
    <>
      {variant === "ghost" ? (
        <button {...buttonProps} type="button" className={`inline-flex items-center gap-1.5 text-[13px] text-[color:var(--gv-ink)]/70 hover:text-[color:var(--gv-pink)] transition-colors ${className}`}>
          <Download size={13} /> {label}
        </button>
      ) : (
        <button {...buttonProps} type="button" className={`btn-secondary ${className}`}>
          <Download size={14} /> {label}
        </button>
      )}
      {open && <CatalogCaptureModal productSlug={productSlug} lang={lang} fallbackUrl={productPdfUrl} t={t} onClose={() => setOpen(false)} />}
    </>
  );
};


const CatalogCaptureModal = ({ productSlug, lang, fallbackUrl, t, onClose }) => {
  const [form, setForm] = useState({ email: "", first_name: "", company: "", category: "" });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) { setErr(t("catalogCapture.err.email")); return; }
    setLoading(true); setErr("");
    const { ok, data, error: err } = await apiFetch(`/api/catalog/request`, {
      method: "POST",
      body: JSON.stringify({
        email: form.email.trim(),
        first_name: form.first_name.trim(),
        company: form.company.trim(),
        category: form.category,
        language: lang,
        product_slug: productSlug || null,
      }),
    });
    if (!ok) {
      setErr(err || "Request failed");
      setLoading(false);
      return;
    }
    onClose();
    // Open the PDF in a new tab — UX-wise that's what they wanted.
    window.open(`${API}${data.pdf_url}`, "_blank", "noopener,noreferrer");
    setLoading(false);
  };

  const skip = () => {
    onClose();
    window.open(fallbackUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="catalog-capture-modal">
      <div className="bg-white rounded-[6px] w-full max-w-md p-6 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-[color:var(--gv-ink)]/50 hover:text-[color:var(--gv-ink)]" aria-label={t("catalogCapture.close")} data-testid="catalog-capture-close">
          <X size={18} />
        </button>
        <div className="mb-5">
          <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-2">{t("catalogCapture.eyebrow")}</div>
          <h2 className="font-display text-2xl text-[color:var(--gv-ink)] leading-tight">{t("catalogCapture.title")}</h2>
          <p className="text-[13.5px] text-[color:var(--gv-ink)]/70 mt-2">{t("catalogCapture.subtitle")}</p>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <input
            type="email"
            required
            value={form.email}
            onChange={(e) => upd("email", e.target.value)}
            placeholder={t("catalogCapture.email")}
            className="w-full border border-[color:var(--gv-line)] rounded-[4px] px-3 py-2.5 text-[14px] focus:border-[color:var(--gv-pink)]/40 outline-none"
            data-testid="catalog-capture-email"
            autoFocus
          />
          <div className="grid grid-cols-2 gap-3">
            <input
              type="text"
              value={form.first_name}
              onChange={(e) => upd("first_name", e.target.value)}
              placeholder={t("catalogCapture.first_name")}
              className="w-full border border-[color:var(--gv-line)] rounded-[4px] px-3 py-2.5 text-[14px] focus:border-[color:var(--gv-pink)]/40 outline-none"
              data-testid="catalog-capture-first_name"
            />
            <input
              type="text"
              value={form.company}
              onChange={(e) => upd("company", e.target.value)}
              placeholder={t("catalogCapture.company")}
              className="w-full border border-[color:var(--gv-line)] rounded-[4px] px-3 py-2.5 text-[14px] focus:border-[color:var(--gv-pink)]/40 outline-none"
              data-testid="catalog-capture-company"
            />
          </div>
          <select
            value={form.category}
            onChange={(e) => upd("category", e.target.value)}
            className="w-full border border-[color:var(--gv-line)] rounded-[4px] px-3 py-2.5 text-[14px] bg-white focus:border-[color:var(--gv-pink)]/40 outline-none"
            data-testid="catalog-capture-category"
          >
            <option value="">{t("catalogCapture.category_placeholder")}</option>
            {CATEGORY_KEYS.map((k) => (
              <option key={k} value={k}>{t(CATEGORY_LABEL_KEYS[k])}</option>
            ))}
          </select>

          {err && <div className="text-rose-600 text-[12.5px]" data-testid="catalog-capture-error">{err}</div>}

          <button type="submit" disabled={loading} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="catalog-capture-submit">
            <Download size={14} /> {loading ? t("catalogCapture.loading") : t("catalogCapture.cta")}
          </button>
          <button type="button" onClick={skip} className="text-[12.5px] text-[color:var(--gv-ink)]/55 hover:text-[color:var(--gv-ink)] block w-full text-center" data-testid="catalog-capture-skip">
            {t("catalogCapture.skip")}
          </button>
        </form>
        <p className="text-[11px] text-[color:var(--gv-ink)]/45 mt-4 text-center leading-relaxed">
          {t("catalogCapture.privacy")}
        </p>
      </div>
    </div>
  );
};

export default DownloadCatalogButton;
